import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';

const PermissionsContext = createContext(null);

// Default permissions (most restrictive)
const DEFAULT_PERMISSIONS = {
  module_inventory: false, module_sales: false, module_production: false,
  module_finance: false, module_hr: false, module_reports: false,
  module_admin: false, module_master_data: false,
  inventory_view: false, inventory_edit: false, inventory_adjust: false,
  inventory_delete: false, inventory_import: false, inventory_export: false,
  po_view: false, po_create: false, po_approve: false, po_send: false, po_delete: false,
  transfer_view: false, transfer_create: false, transfer_receive: false, transfer_approve: false,
  reports_view: false, reports_export: false, reports_create_custom: false, reports_schedule: false,
  recipes_view: false, recipes_manage: false, production_log: false, production_approve: false,
  sales_view: false, sales_edit: false, sales_refund: false, sales_discount: false, sales_void: false,
  finance_view: false, finance_edit: false, finance_approve: false, finance_journal: false,
  branches_view: false, branches_manage: false,
  suppliers_view: false, suppliers_manage: false,
  hr_view: false, hr_employees_manage: false, hr_leave_manage: false,
  hr_attendance_manage: false, hr_payroll_view: false, hr_payroll_manage: false,
  hr_performance_manage: false,
  settings_view: false, settings_manage: false,
  users_view: false, users_manage: false,
  data_import: false, data_export: false, data_backup: false,
};

export function PermissionsProvider({ children, user }) {
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchPermissions();
    } else {
      setPermissions(DEFAULT_PERMISSIONS);
      setLoading(false);
    }
  }, [user]);

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/users/me/permissions`);
      setPermissions({ ...DEFAULT_PERMISSIONS, ...response.data });
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // If admin or super_admin, grant all permissions as fallback
      if (user?.role === 'admin' || user?.role === 'super_admin') {
        const allPermissions = {};
        Object.keys(DEFAULT_PERMISSIONS).forEach(key => {
          allPermissions[key] = true;
        });
        setPermissions(allPermissions);
      }
    } finally {
      setLoading(false);
    }
  };

  const can = (permission) => {
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    return permissions[permission] === true;
  };

  const canModule = (moduleKey) => {
    if (user?.role === 'admin' || user?.role === 'super_admin') return true;
    return permissions[moduleKey] === true;
  };

  const canAny = (...permissionList) => {
    return permissionList.some(p => can(p));
  };

  const canAll = (...permissionList) => {
    return permissionList.every(p => can(p));
  };

  return (
    <PermissionsContext.Provider value={{ 
      permissions, 
      loading, 
      can, 
      canModule,
      canAny, 
      canAll,
      isAdmin: user?.role === 'admin' || user?.role === 'super_admin',
      isHRManager: user?.role === 'hr_manager',
      isManager: ['manager', 'hr_manager', 'finance_manager', 'inventory_manager'].includes(user?.role),
      refresh: fetchPermissions
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
}

export default PermissionsContext;
